import type { OtuBoldDetail } from '../../../Generated/OtuBoldDetail.ts'
import type { OtuDetail } from '../../../Generated/OtuDetail.ts'
import type { OtuSourceDetail } from '../../../Generated/OtuSourceDetail.ts'
import { BlastSource } from '../../../Model/Project/BlastSource.js'
import { OTU_IDS_PER_REQUEST } from '../../../Repository/Visualization/OtuCubes.js'
import { displayTaxon } from './OtuDataTableView.logic.js'

export const RANK_LABELS = ['Domain', 'Kingdom', 'Phylum', 'Class', 'Order', 'Family', 'Genus', 'Species']

/** The XLSX colours an RDP support below this as failed. */
export const RDP_SUPPORT_THRESHOLD = 0.3

/** Details per OTU id; `null` for an id the backend did not return (not an OTU of the scope the user may see). */
export type DetailsCache = ReadonlyMap<string, OtuDetail | null>

export function chunks<T>(items: readonly T[], size: number = OTU_IDS_PER_REQUEST): T[][] {
  const out: T[][] = []
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size))
  return out
}

/** The requests that fetch those of [ids] neither cached nor already on their way, each id once. */
export function idsToFetch(ids: readonly string[], cache: DetailsCache, pending: ReadonlySet<string>): string[][] {
  const missing = [...new Set(ids)].filter((id) => !cache.has(id) && !pending.has(id))
  return chunks(missing)
}

/** [cache] with a request's answer, recording each requested id the answer lacks as `null`. */
export function withDetails(cache: DetailsCache, requested: readonly string[], received: readonly OtuDetail[]): DetailsCache {
  const next = new Map(cache)
  for (const id of requested) next.set(id, null)
  for (const detail of received) next.set(detail.otuUuid, detail)
  return next
}

const SOURCE_ORDER: BlastSource[] = Object.values(BlastSource)

/** The OTU's sources to show side by side: those of [scopeSources] when known, in its order, else all, in enum order. */
export function sourceColumns(detail: OtuDetail, scopeSources: readonly BlastSource[] | null): OtuSourceDetail[] {
  const order = scopeSources ?? SOURCE_ORDER
  return (detail.sources ?? [])
    .filter((s) => order.includes(s.source as BlastSource))
    .sort((a, b) => order.indexOf(a.source as BlastSource) - order.indexOf(b.source as BlastSource))
}

/** One rank of a source: the top hit's taxon, and the alternative hit's when it names another. */
export function ladderCell(source: OtuSourceDetail, rank: number): { top: string | null; alt: string | null } {
  const top = displayTaxon(source.topLadder?.ranks?.[rank])
  const alt = displayTaxon(source.altLadder?.ranks?.[rank])
  return { top, alt: alt !== null && alt !== top ? alt : null }
}

/** Where a source's process id leads: the NCBI accession or the BOLD record. */
export function processIdUrl(source: OtuSourceDetail): string | null {
  const id = source.processId?.trim()
  if (!id) return null
  if (source.source === BlastSource.NCBI) return `https://www.ncbi.nlm.nih.gov/nuccore/${encodeURIComponent(id)}`
  if (source.source === BlastSource.BOLD) {
    return `https://www.boldsystems.org/index.php/Public_RecordView?processid=${encodeURIComponent(id)}`
  }
  return null
}

/** The species named by the Consensus ladder if it has one, else by the first source's; for the GBIF link. */
export function gbifSpecies(columns: readonly OtuSourceDetail[], detail: OtuDetail): string | null {
  const consensus = (detail.sources ?? []).find((s) => s.source === BlastSource.Consensus)
  const from = consensus && displayTaxon(consensus.topLadder?.ranks?.[7]) ? consensus : columns[0]
  const species = displayTaxon(from?.topLadder?.ranks?.[7])
  return species ? species.replace(/_/g, ' ') : null
}

export const rdpFails = (support: number | null | undefined): boolean =>
  support !== null && support !== undefined && support < RDP_SUPPORT_THRESHOLD

export function binUrl(bold: OtuBoldDetail): string | null {
  if (bold.boldLink?.startsWith('http')) return bold.boldLink
  return bold.binUri ? `https://www.boldsystems.org/index.php/Public_BarcodeCluster?clusteruri=${bold.binUri}` : null
}
